import { useRef, useState } from "react";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { BaseInput, Select } from "@smart/itops-sb-design-system-dom";

import {
  DynamicOptionsPlaceholder,
  shouldShowDynamicOptionsPlaceholder,
} from "./dynamic-options-placeholder";
import { FieldGroup } from "./field-group";
import { FieldSet } from "./input";
import { optionsWithFallback } from "./item";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { fieldName } from "../../hooks";

export const ListField = ({
  field,
  index,
  value,
  error,
  loading,
  disabled,
  onChange,
  onBlur,
  isPreview,
}: FieldProps<HTMLInputElement, string | null>) => {
  const { dynamicOptionList } = useFeatureFlags();
  const otherRef = useRef<HTMLInputElement>(null);
  const fieldOptionsWithFallback = optionsWithFallback(field.options);
  const otherOption = { label: "Other", value: "__other__" };
  const options = field.allowCustomResponse
    ? [...fieldOptionsWithFallback, otherOption]
    : fieldOptionsWithFallback;
  const [other, setOther] = useState(
    () => !!value && !options.find((o) => o.value === value),
  );

  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  const handleSelectChange = (selected: string) => {
    if (selected === otherOption.value) {
      setOther(!other);
      onChange(null);
    } else if (selected === value) {
      // Reset value if "unselected"
      onChange(null);
    } else {
      setOther(false);
      onChange(selected ?? null);
    }
    onBlur();
  };

  if (dynamicOptionList && shouldShowDynamicOptionsPlaceholder(field)) {
    return (
      <DynamicOptionsPlaceholder
        field={field}
        index={index}
        error={error}
        loading={loading}
        disabled={disabled}
        isPreview={isPreview}
      />
    );
  }

  return (
    <FieldWrapper disabled={disabled} isLoading={loading}>
      <FieldGroup
        id={id}
        hint={field.hint ?? undefined}
        label={<Label {...field} />}
      >
        <FieldSet
          role="checkbox"
          uri={field.uri}
          error={error}
          errorId={errorId}
        >
          <Select
            id={id}
            value={other ? otherOption.value : value}
            onChange={handleSelectChange}
            options={options}
            disabled={disabled}
            error={!!error}
            aria-errormessage={error && errorId}
            dataTestId="list-field-multiselect"
          />
          {field.allowCustomResponse && other && (
            <BaseInput
              ref={otherRef}
              name={fieldName({ field, index, suffix: "other-input" })}
              type="text"
              autoFocus
              value={other ? value || "" : ""}
              aria-hidden={!other}
              aria-label="Other Value"
              disabled={disabled}
              onChange={(e) => {
                onChange(e.currentTarget.value);
              }}
              onBlur={onBlur}
            />
          )}
        </FieldSet>
      </FieldGroup>
    </FieldWrapper>
  );
};
